import type { ViewerShell } from './shell';
import { registerCustomTool } from './tools';
import type { Viewport } from './viewport';

/**
 * Cross-viewport behaviour (spec sections 1.2 and 2):
 * - "Sincronizar" (link, on by default): stacks of the same study follow each other by patient
 *   position on every new image (cornerstone-tools `stackImagePositionSynchronizer`).
 * - "Manual": any other pair (other studies, or everything while link is off) keeps the position
 *   offset captured when it was switched on (`stackImagePositionOffsetSynchronizer`).
 * - Reference lines: the focused viewport's plane drawn on every other viewport (legacy
 *   `referenceLines` tool), re-evaluated whenever the focus moves.
 * - Triangulation (tool id 'triangulation'): a click sets a 3D point; every other viewport of the
 *   same frame of reference jumps to its nearest slice and marks the point with a small crosshair.
 */

// --- cornerstone-tools legacy APIs used here (typed locally) ------------------------------------

type SyncHandler = (sync: Synchronizer, source: HTMLElement, target: HTMLElement, eventData?: unknown, positionDifference?: unknown) => void;

interface Synchronizer {
  add(element: HTMLElement): void;
  addSource(element: HTMLElement): void;
  addTarget(element: HTMLElement): void;
  destroy(): void;
  getSourceElements(): HTMLElement[];
  getTargetElements(): HTMLElement[];
}

type SynchronizerCtor = new (event: string, handler: SyncHandler) => Synchronizer;

interface ReferenceLinesTool {
  enable(element: HTMLElement, synchronizer: Synchronizer): void;
  disable(element: HTMLElement): void;
}

interface ImagePlane {
  frameOfReferenceUID?: string;
  rowCosines?: number[];
  columnCosines?: number[];
  imagePositionPatient?: number[];
  rowPixelSpacing?: number;
  columnPixelSpacing?: number;
  sliceThickness?: number;
}

type Vec3 = [number, number, number];

interface ToolEventDetail {
  element: HTMLElement;
  currentPoints?: { image: { x: number; y: number } };
  canvasContext?: CanvasRenderingContext2D;
}

function lib() {
  const tools = window.cornerstoneTools;
  return {
    Synchronizer: tools.Synchronizer as SynchronizerCtor,
    position: tools.stackImagePositionSynchronizer as SyncHandler,
    positionOffset: tools.stackImagePositionOffsetSynchronizer as SyncHandler,
    updateImage: tools.updateImageSynchronizer as SyncHandler,
    referenceLines: tools.referenceLines.tool as ReferenceLinesTool,
  };
}

// --- State --------------------------------------------------------------------------------------

let shellRef: ViewerShell | null = null;
let linked = true;
let manualLinked = false;
let referenceLines = true;
let positionSync: Synchronizer | null = null;
let referenceSync: Synchronizer | null = null;
const referenceElements = new Set<HTMLElement>();
/** Set while synchronizers are torn down: their removal callbacks must not move any stack. */
let suspended = false;

const NEW_IMAGE = () => window.cornerstone.EVENTS.NEW_IMAGE;
/** Planes more than ~30° apart are not position-synchronised (their positions do not correspond). */
const PARALLEL_COS = Math.cos(Math.PI / 6);

// --- Geometry -----------------------------------------------------------------------------------

function vec(values: number[] | undefined): Vec3 | undefined {
  if (!values || values.length < 3) return undefined;
  const v: Vec3 = [Number(values[0]), Number(values[1]), Number(values[2])];
  return v.every(Number.isFinite) ? v : undefined;
}

const dot = (a: Vec3, b: Vec3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const sub = (a: Vec3, b: Vec3): Vec3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const cross = (a: Vec3, b: Vec3): Vec3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];

function planeOf(imageId: string | undefined): ImagePlane | undefined {
  if (!imageId) return undefined;
  return window.cornerstone.metaData.get('imagePlaneModule', imageId) as ImagePlane | undefined;
}

interface Geometry {
  origin: Vec3;
  row: Vec3;
  column: Vec3;
  normal: Vec3;
  rowSpacing: number;
  columnSpacing: number;
  thickness: number;
  frameOfReferenceUID: string;
}

function geometryOf(imageId: string | undefined): Geometry | undefined {
  const plane = planeOf(imageId);
  const origin = vec(plane?.imagePositionPatient);
  const row = vec(plane?.rowCosines);
  const column = vec(plane?.columnCosines);
  if (!plane || !origin || !row || !column) return undefined;
  return {
    origin,
    row,
    column,
    normal: cross(row, column),
    rowSpacing: plane.rowPixelSpacing || 1,
    columnSpacing: plane.columnPixelSpacing || 1,
    thickness: plane.sliceThickness || 0,
    frameOfReferenceUID: plane.frameOfReferenceUID ?? '',
  };
}

/** Image pixel (x = column, y = row) → patient coordinates (mm). */
function imageToPatient(g: Geometry, point: { x: number; y: number }): Vec3 {
  return [0, 1, 2].map((i) => g.origin[i] + g.row[i] * point.x * g.columnSpacing + g.column[i] * point.y * g.rowSpacing) as Vec3;
}

/** Patient point → image pixel of this plane, plus its distance to the plane (mm). */
function patientToImage(g: Geometry, point: Vec3): { x: number; y: number; distance: number } {
  const d = sub(point, g.origin);
  return { x: dot(d, g.row) / g.columnSpacing, y: dot(d, g.column) / g.rowSpacing, distance: Math.abs(dot(d, g.normal)) };
}

function sameFrame(a: string, b: string): boolean {
  return !a || !b || a === b;
}

// --- Viewport helpers ---------------------------------------------------------------------------

function viewportOf(element: HTMLElement): Viewport | undefined {
  return shellRef?.viewports().find((vp) => vp.element === element);
}

function currentImageId(viewport: Viewport | undefined): string | undefined {
  try {
    return viewport?.currentImage()?.imageId;
  } catch {
    return undefined;
  }
}

function readyViewports(): Viewport[] {
  return (shellRef?.viewports() ?? []).filter((vp) => vp.currentStack && currentImageId(vp));
}

function parallel(a: Viewport, b: Viewport): boolean {
  const ga = geometryOf(currentImageId(a));
  const gb = geometryOf(currentImageId(b));
  if (!ga || !gb) return false;
  return Math.abs(dot(ga.normal, gb.normal)) >= PARALLEL_COS;
}

// --- Link ("Sincronizar") and manual sync -------------------------------------------------------

function studyOf(viewport: Viewport): string | undefined {
  return viewport.currentStack?.series.studyInstanceUID;
}

function positionHandler(sync: Synchronizer, source: HTMLElement, target: HTMLElement, eventData?: unknown, positionDifference?: unknown): void {
  if (suspended || source === target) return;
  const from = viewportOf(source);
  const to = viewportOf(target);
  if (!from || !to || !currentImageId(from) || !currentImageId(to) || !to.currentStack) return;
  const byLink = linked && studyOf(from) === studyOf(to);
  try {
    if (byLink) {
      if (parallel(from, to)) lib().position(sync, source, target);
    } else if (manualLinked && positionDifference && typeof positionDifference === 'object') {
      lib().positionOffset(sync, source, target, eventData, positionDifference);
    }
  } catch (error) {
    console.warn('[viewer] sync failed', error);
  }
}

function destroySync(sync: Synchronizer | null): void {
  if (!sync) return;
  suspended = true;
  try {
    sync.destroy();
  } catch (error) {
    console.warn('[viewer] synchronizer teardown', error);
  } finally {
    suspended = false;
  }
}

/**
 * One synchronizer on the new-image event for every loaded viewport; the handler decides per pair
 * whether the link (same study, by position) or the manual offset applies. Creating it captures the
 * manual offsets between the positions on screen right now.
 */
function rebuildPositionSync(): void {
  destroySync(positionSync);
  positionSync = null;
  if (!linked && !manualLinked) return;
  const viewports = readyViewports();
  if (viewports.length < 2) return;
  suspended = true;
  try {
    positionSync = new (lib().Synchronizer)(NEW_IMAGE(), positionHandler);
    for (const viewport of viewports) positionSync.add(viewport.element);
  } finally {
    suspended = false;
  }
}

/** "Sincronizar series": same-study stacks follow each other by position. */
export function setLink(on: boolean): void {
  linked = on;
  rebuildPositionSync();
}

export function isLinked(): boolean {
  return linked;
}

/** "Sincronizar series de diferentes estudios a partir de posición inicial". */
export function setManualLink(on: boolean): void {
  manualLinked = on;
  rebuildPositionSync();
}

export function isManualLinked(): boolean {
  return manualLinked;
}

// --- Reference lines ----------------------------------------------------------------------------

function clearReferenceLines(): void {
  const tool = lib().referenceLines;
  for (const element of referenceElements) {
    try {
      tool.disable(element);
      window.cornerstoneTools.clearToolState(element, 'referenceLines');
    } catch {
      // The element was disabled with its viewport; nothing left to clean.
    }
  }
  referenceElements.clear();
  destroySync(referenceSync);
  referenceSync = null;
}

/** Focused viewport = source; every other loaded viewport draws its plane (IMEXHS `focusViewer`). */
function rebuildReferenceLines(): void {
  clearReferenceLines();
  const shell = shellRef;
  const active = shell?.activeViewport();
  if (!referenceLines || !shell || !active || !currentImageId(active)) return;
  const targets = readyViewports().filter((vp) => vp !== active);
  if (targets.length === 0) return;
  const { Synchronizer, updateImage, referenceLines: tool } = lib();
  suspended = true;
  try {
    referenceSync = new Synchronizer(NEW_IMAGE(), (sync, source, target) => {
      if (source !== target) updateImage(sync, source, target);
    });
    referenceSync.addSource(active.element);
    for (const target of targets) {
      referenceSync.addTarget(target.element);
      window.cornerstoneTools.clearToolState(target.element, 'referenceLines');
      tool.enable(target.element, referenceSync);
      referenceElements.add(target.element);
    }
  } finally {
    suspended = false;
  }
}

/** "Líneas de referencia" toggle (on by default). */
export function setReferenceLines(on: boolean): void {
  referenceLines = on;
  rebuildReferenceLines();
}

export function areReferenceLinesOn(): boolean {
  return referenceLines;
}

// --- Triangulation ------------------------------------------------------------------------------

const TRIANGULATION_TOOL = 'Triangulation';
const MARKER_HALF = 8;
const MARKER_GAP = 2;
const MARKER_COLOR = 'rgb(255, 255, 0)';
/** Marks drawn only on slices within this distance (mm) when the slice thickness is unknown. */
const DEFAULT_MARK_DISTANCE = 2;

interface TriangulationPoint {
  patient: Vec3;
  frameOfReferenceUID: string;
}

let triangulationPoint: TriangulationPoint | null = null;

/** Index of the stack image whose plane is nearest to `point` (images without geometry are skipped). */
function nearestSlice(imageIds: string[], point: Vec3): number {
  let best = -1;
  let bestDistance = Number.POSITIVE_INFINITY;
  imageIds.forEach((imageId, index) => {
    const g = geometryOf(imageId);
    if (!g) return;
    const distance = patientToImage(g, point).distance;
    if (distance < bestDistance) {
      bestDistance = distance;
      best = index;
    }
  });
  return best;
}

/** Sets the 3D point under `imagePoint` of `element` and moves the other viewports onto it. */
export function triangulate(element: HTMLElement, imagePoint: { x: number; y: number }): void {
  const source = viewportOf(element);
  const g = geometryOf(currentImageId(source));
  if (!source || !g) return;
  const point: TriangulationPoint = { patient: imageToPatient(g, imagePoint), frameOfReferenceUID: g.frameOfReferenceUID };
  triangulationPoint = point;
  for (const target of readyViewports()) {
    if (target === source || !target.currentStack) continue;
    const tg = geometryOf(currentImageId(target));
    if (!tg || !sameFrame(tg.frameOfReferenceUID, point.frameOfReferenceUID)) continue;
    const index = nearestSlice(target.currentStack.imageIds, point.patient);
    if (index >= 0 && index !== target.currentIndex()) window.cornerstoneTools.scrollToIndex(target.element, index);
    else window.cornerstone.updateImage(target.element);
  }
  window.cornerstone.updateImage(element);
}

export function triangulationTarget(): Readonly<TriangulationPoint> | null {
  return triangulationPoint;
}

function drawTriangulationMark(detail: ToolEventDetail): void {
  const point = triangulationPoint;
  const context = detail.canvasContext?.canvas.getContext('2d');
  const g = geometryOf(currentImageId(viewportOf(detail.element)));
  if (!point || !context || !g || !sameFrame(g.frameOfReferenceUID, point.frameOfReferenceUID)) return;
  const projected = patientToImage(g, point.patient);
  if (projected.distance > Math.max(g.thickness, DEFAULT_MARK_DISTANCE)) return;
  const pixelToCanvas = (window.cornerstone as unknown as { pixelToCanvas(el: HTMLElement, p: { x: number; y: number }): { x: number; y: number } }).pixelToCanvas;
  const c = pixelToCanvas(detail.element, projected);
  context.save();
  context.setTransform(1, 0, 0, 1, 0, 0);
  context.strokeStyle = MARKER_COLOR;
  context.lineWidth = 1;
  context.beginPath();
  for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
    context.moveTo(c.x + dx * MARKER_GAP, c.y + dy * MARKER_GAP);
    context.lineTo(c.x + dx * MARKER_HALF, c.y + dy * MARKER_HALF);
  }
  context.stroke();
  context.restore();
}

type BaseToolCtor = new (props: Record<string, unknown>) => object;

/** Builds the tool on cornerstone-tools' BaseTool (not exported by 3.0.0-beta; taken from PanTool's prototype chain). */
function createTriangulationTool(): BaseToolCtor | undefined {
  const pan = window.cornerstoneTools.PanTool as BaseToolCtor | undefined;
  const Base = pan ? (Object.getPrototypeOf(pan) as BaseToolCtor) : undefined;
  if (!Base || typeof Base !== 'function') return undefined;
  const onPoint = (event: CustomEvent<ToolEventDetail>): boolean => {
    const { element, currentPoints } = event.detail;
    if (currentPoints) triangulate(element, currentPoints.image);
    return true;
  };
  return class TriangulationTool extends Base {
    constructor() {
      super({ name: TRIANGULATION_TOOL, supportedInteractionTypes: ['mouse', 'touch'] });
    }

    postMouseDownCallback(event: CustomEvent<ToolEventDetail>): boolean {
      return onPoint(event);
    }

    mouseDragCallback(event: CustomEvent<ToolEventDetail>): boolean {
      return onPoint(event);
    }

    postTouchStartCallback(event: CustomEvent<ToolEventDetail>): boolean {
      return onPoint(event);
    }

    touchDragCallback(event: CustomEvent<ToolEventDetail>): boolean {
      return onPoint(event);
    }

    renderToolData(event: CustomEvent<ToolEventDetail>): void {
      drawTriangulationMark(event.detail);
    }
  };
}

function registerTriangulation(): void {
  const tool = createTriangulationTool();
  if (!tool) {
    console.warn('[viewer] triangulation unavailable: no cornerstone-tools base class');
    return;
  }
  try {
    registerCustomTool('triangulation', tool, TRIANGULATION_TOOL);
  } catch (error) {
    console.warn('[viewer] triangulation registration failed', error);
  }
}

// --- Install ------------------------------------------------------------------------------------

function rebuildAll(): void {
  rebuildPositionSync();
  rebuildReferenceLines();
}

export function install(shell: ViewerShell): void {
  shellRef = shell;
  shell.on('layout-changed', () => {
    triangulationPoint = null;
    rebuildAll();
  });
  shell.on('series-shown', rebuildAll);
  shell.on('active-changed', rebuildReferenceLines);
  registerTriangulation();
}
